import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Calendar, Image, Users, ArrowRight, Instagram, Facebook, Globe, Twitter, Menu, X, Video, Plane, Heart, Briefcase, GraduationCap, Music, PartyPopper, Sparkles, Mail, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { API, useAuth } from "../App";
import SEO from "../components/SEO";
import BrandingFooter from "../components/BrandingFooter";
import FloatingBrandingBadge from "../components/FloatingBrandingBadge";
import PhotographerContactModal from "../components/PhotographerContactModal";

const PhotographerLandingPage = () => {
  const { businessName } = useParams();
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [retryAttempted, setRetryAttempted] = useState(false);
  const { user } = useAuth();

  // Icon mapping for event types
  const getEventTypeIcon = (eventType) => {
    const iconMap = {
      "wedding": <Heart className="w-8 h-8" />,
      "sports": <Users className="w-8 h-8" />,
      "portrait": <Camera className="w-8 h-8" />,
      "portraits": <Camera className="w-8 h-8" />,
      "headshot": <Camera className="w-8 h-8" />,
      "headshots": <Camera className="w-8 h-8" />,
      "corporate": <Briefcase className="w-8 h-8" />,
      "event": <PartyPopper className="w-8 h-8" />,
      "events": <PartyPopper className="w-8 h-8" />,
      "graduation": <GraduationCap className="w-8 h-8" />,
      "concert": <Music className="w-8 h-8" />,
      "music": <Music className="w-8 h-8" />,
      "travel": <Plane className="w-8 h-8" />,
      "photoshoot": <Sparkles className="w-8 h-8" />
    };
    
    const lowerType = eventType?.toLowerCase() || "";
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerType.includes(key)) return icon;
    }
    return <Image className="w-8 h-8" />;
  };

  // Icon mapping for service/request types
  const getServiceTypeIcon = (serviceType) => {
    const iconMap = {
      "photos": <Camera className="w-6 h-6" />,
      "photo": <Camera className="w-6 h-6" />,
      "video": <Video className="w-6 h-6" />,
      "videography": <Video className="w-6 h-6" />,
      "drone": <Plane className="w-6 h-6" />,
      "aerial": <Plane className="w-6 h-6" />,
      "editing": <Sparkles className="w-6 h-6" />,
      "retouching": <Sparkles className="w-6 h-6" />
    };
    
    const lowerType = serviceType?.toLowerCase() || "";
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerType.includes(key)) return icon;
    }
    return <Camera className="w-6 h-6" />;
  };

  // Generate description for event type
  const getEventDescription = (eventType) => {
    const descriptions = {
      "wedding": "Capturing your special day with timeless elegance and emotion.",
      "sports": "Action-packed coverage of games, tournaments, and athletic events.",
      "portrait": "Professional portraits that capture your unique personality.",
      "portraits": "Professional portraits that capture your unique personality.",
      "headshot": "Polished headshots for professionals and executives.",
      "headshots": "Polished headshots for professionals and executives.",
      "corporate": "Professional photography for business events and branding.",
      "event": "Complete coverage of your special occasions and celebrations.",
      "events": "Complete coverage of your special occasions and celebrations.",
      "graduation": "Commemorating academic achievements with stunning imagery.",
      "concert": "Dynamic shots capturing the energy of live performances.",
      "music": "Dynamic shots capturing the energy of live performances.",
      "travel": "Breathtaking imagery from destinations around the world.",
      "photoshoot": "Custom photo sessions tailored to your creative vision."
    };
    
    const lowerType = eventType?.toLowerCase() || "";
    for (const [key, desc] of Object.entries(descriptions)) {
      if (lowerType.includes(key)) return desc;
    }
    return "Professional photography services tailored to your needs.";
  };

  // Default hero images
  const defaultImages = [
    "https://images.unsplash.com/photo-1597759337613-046b458dca56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    "https://images.unsplash.com/photo-1612065460245-81407b175d79?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.unsplash.com/photo-1752681304917-453df272eed7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.unsplash.com/photo-1626473401641-1ec479dd4f0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.unsplash.com/photo-1567324216285-61f36e5b719f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  ];

  const fetchPhotographer = useCallback(async () => {
    setLoading(true);
    setError(null);

    const run = async () => {
      // IMPORTANT: Do not convert underscores to spaces.
      // Backend supports slug matching already.
      const response = await axios.get(
        `${API}/photographer/by-business-name/${encodeURIComponent(businessName)}`
      );
      return response;
    };

    try {
      const response = await run();
      setPhotographer(response.data);

      // Set portfolio photos from photographer's featured album or defaults
      if (response.data.portfolio_photos && response.data.portfolio_photos.length > 0) {
        const token = response.data.portfolio_token;
        const albumId = response.data.featured_album_id;
        const photos = response.data.portfolio_photos.map(photo => ({
          src: `${API}/media/${albumId}/${photo.filename}${token ? `?token=${token}` : ''}`,
          alt: photo.filename
        }));
        setPortfolioPhotos(photos);
      } else {
        setPortfolioPhotos(defaultImages.map((src, i) => ({ src, alt: `Portfolio ${i + 1}` })));
      }

      // Reset retry flag on success
      setRetryAttempted(false);

    } catch (err) {
      const status = err?.response?.status;

      // If not found, show a friendly not-found state (no redirect)
      if (status === 404) {
        setError({
          type: "not_found",
          message: "Photographer not found."
        });
        return;
      }

      // Transient server/network errors - retry once
      const transientStatuses = [520, 502, 503, 504];
      const isNetworkError = !err?.response;

      if (!retryAttempted && (transientStatuses.includes(status) || isNetworkError)) {
        setRetryAttempted(true);
        await new Promise(r => setTimeout(r, 600));
        try {
          const response = await run();
          setPhotographer(response.data);

          if (response.data.portfolio_photos && response.data.portfolio_photos.length > 0) {
            const token = response.data.portfolio_token;
            const albumId = response.data.featured_album_id;
            const photos = response.data.portfolio_photos.map(photo => ({
              src: `${API}/media/${albumId}/${photo.filename}${token ? `?token=${token}` : ''}`,
              alt: photo.filename
            }));
            setPortfolioPhotos(photos);
          } else {
            setPortfolioPhotos(defaultImages.map((src, i) => ({ src, alt: `Portfolio ${i + 1}` })));
          }

          // Success after retry
          setError(null);
          return;

        } catch (err2) {
          // fall through to error state
        }
      }

      console.error("Failed to fetch photographer:", err);
      setError({
        type: "temporary",
        message: "We're having trouble loading this profile. Please try again."
      });

    } finally {
      setLoading(false);
    }
  }, [businessName, retryAttempted]);

  useEffect(() => {
    fetchPhotographer();
  }, [fetchPhotographer]);

  // Hero image cycling effect - every 30 seconds with fade
  useEffect(() => {
    if (portfolioPhotos.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentHeroIndex(prev => (prev + 1) % portfolioPhotos.length);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [portfolioPhotos.length]);

  const getSocialIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'twitter':
      case 'x': return <Twitter className="w-5 h-5" />;
      case 'website': return <Globe className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  // Get branding
  const brandName = photographer?.company_name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;
  const tagline = photographer?.tagline || photographer?.company_name || "Capturing Moments That Matter";
  const bio = photographer?.bio || "Professional photography services for sports, weddings, events, and more. Let's create something beautiful together.";

  // Get hero images - use portfolio photos if available
  const heroImages = portfolioPhotos.length > 0 
    ? portfolioPhotos.map(p => p.src)
    : defaultImages;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // Error states - show inline UI instead of redirecting
  if (error?.type === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Photographer not found</h1>
          <p className="text-muted-foreground mb-6">
            This profile may have been moved or is unavailable.
          </p>
          <Button
            onClick={() => window.location.href = "/"}
            className="btn-primary"
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  if (error?.type === "temporary") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="max-w-md text-center">
          <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Could not load this profile</h1>
          <p className="text-muted-foreground mb-6">{error.message}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setRetryAttempted(false);
                fetchPhotographer();
              }}
              className="btn-primary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = "/"}
            >
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Photographer not found</h1>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title={`${photographer.business_name || photographer.name} | Professional Photography`}
        description={photographer.bio || `Book a session with ${photographer.business_name || photographer.name}. Professional photography services including ${photographer.specialties?.join(', ') || 'portraits, events, and more'}.`}
        canonical={`https://www.360lumina.com/photographer/${businessName}`}
        type="profile"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": photographer.business_name || photographer.name,
          "description": photographer.bio,
          "image": photographer.avatar_url,
          "url": `https://www.360lumina.com/photographer/${businessName}`,
          "@id": `https://www.360lumina.com/photographer/${businessName}`,
          "priceRange": "$$"
        }}
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/photographer/${businessName}`} className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-8 max-w-[160px] object-contain" />
            ) : (
              <>
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-2xl font-serif tracking-tight">{brandName}</span>
              </>
            )}
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to={`/galleries?photographer=${photographer.photographer_id}`} 
              className="nav-link" 
              data-testid="nav-galleries"
            >
              Galleries
            </Link>
            <Link 
              to={`/client-login/${photographer.photographer_id}`} 
              className="nav-link" 
              data-testid="nav-client-portal"
            >
              Client Portal
            </Link>
            {user && (
              <Link 
                to="/help" 
                className="nav-link" 
                data-testid="nav-help"
              >
                Help
              </Link>
            )}
            {user ? (
              <Link to="/dashboard">
                <Button className="btn-primary rounded-sm" data-testid="nav-dashboard">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="btn-secondary rounded-sm" data-testid="nav-login">
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-background/95 backdrop-blur-md">
            <div className="px-6 py-4 space-y-4">
              <Link 
                to={`/galleries?photographer=${photographer.photographer_id}`}
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Galleries
              </Link>
              <Link 
                to={`/client-login/${photographer.photographer_id}`}
                className="block text-lg text-muted-foreground hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Client Portal
              </Link>
              {user && (
                <Link 
                  to="/help"
                  className="block text-lg text-muted-foreground hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Help
                </Link>
              )}
              {user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-primary rounded-sm w-full">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-secondary rounded-sm w-full">
                    Login
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Cycling Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentHeroIndex}
              src={heroImages[currentHeroIndex]}
              alt="Professional photographer"
              className="w-full h-full object-cover opacity-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
            />
          </AnimatePresence>
          <div className="hero-gradient absolute inset-0" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-6 max-w-4xl"
        >
          <p className="text-sm uppercase tracking-widest text-primary mb-4">
            Professional Photography
          </p>
          <h1 className="text-5xl md:text-7xl tracking-tight leading-none mb-6">
            {tagline}
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {bio}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/book/${photographer.photographer_id}`}>
              <Button className="btn-primary rounded-sm" data-testid="hero-book-now">
                Book Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to={`/galleries?photographer=${photographer.photographer_id}`}>
              <Button className="btn-secondary rounded-sm" data-testid="hero-view-galleries">
                View Galleries
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Portfolio Grid - No "View" overlay */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest text-primary mb-4">Portfolio</p>
            <h2 className="text-4xl md:text-5xl tracking-tight">Selected Works</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {portfolioPhotos.slice(0, 6).map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative aspect-square overflow-hidden rounded-sm group cursor-pointer"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Subtle highlight on hover - no text */}
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - What I Offer (Event Types) */}
      <section className="py-24 px-6 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-widest text-primary mb-4">Services</p>
            <h2 className="text-4xl md:text-5xl tracking-tight">What I Offer</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(photographer?.event_types?.length > 0 
              ? photographer.event_types.slice(0, 4) 
              : ["Sports Photography", "Wedding & Events", "Portraits & Headshots", "Corporate Events"]
            ).map((eventType, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card border border-border rounded-sm p-8 card-hover"
                data-testid={`event-type-${index}`}
              >
                <div className="text-primary mb-4">{getEventTypeIcon(eventType)}</div>
                <h3 className="text-xl mb-3">{eventType}</h3>
                <p className="text-muted-foreground text-sm">{getEventDescription(eventType)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Types Section (Request Types - Photos, Video, Drone, etc.) */}
      {photographer?.request_types?.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="text-sm uppercase tracking-widest text-primary mb-4">Capabilities</p>
              <h2 className="text-4xl md:text-5xl tracking-tight">Service Types</h2>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-4">
              {photographer.request_types.map((serviceType, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3 bg-card border border-border rounded-full px-6 py-3 hover:border-primary/50 transition-colors"
                  data-testid={`service-type-${index}`}
                >
                  <span className="text-primary">{getServiceTypeIcon(serviceType)}</span>
                  <span className="font-medium">{serviceType}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={photographer.avatar_url 
                  ? `${API.replace("/api", "")}${photographer.avatar_url}` 
                  : photographer.user?.picture || defaultImages[0]}
                alt={photographer.user?.name}
                className="w-full aspect-[4/5] object-cover rounded-sm"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-widest text-primary mb-4">About</p>
              <h2 className="text-4xl md:text-5xl tracking-tight mb-6">
                {photographer.user?.name || "The Photographer"}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {bio}
              </p>
              
              {/* Social Links */}
              {photographer.social_links && Object.keys(photographer.social_links).length > 0 && (
                <div className="flex gap-4 mb-8">
                  {Object.entries(photographer.social_links).map(([platform, url]) => (
                    url && (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 border border-white/20 rounded-sm flex items-center justify-center text-muted-foreground hover:text-white hover:border-white/40 transition-colors"
                      >
                        {getSocialIcon(platform)}
                      </a>
                    )
                  ))}
                </div>
              )}

              <Link to={`/book/${photographer.photographer_id}`}>
                <Button className="btn-primary rounded-sm" data-testid="about-book-now">
                  Book a Session
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-primary/5 border-t border-b border-primary/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl tracking-tight mb-6">
              Ready to Create Something Amazing?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let&apos;s discuss your vision and bring it to life through stunning photography.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/book/${photographer.photographer_id}`}>
                <Button className="btn-primary rounded-sm" data-testid="cta-book-now">
                  Book Now
                </Button>
              </Link>
              <Button 
                className="btn-secondary rounded-sm" 
                onClick={() => setContactModalOpen(true)}
                data-testid="cta-contact"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt={brandName} className="h-6 max-w-[120px] object-contain" />
            ) : (
              <>
                <Camera className="w-6 h-6 text-primary" />
                <span className="text-xl font-serif">{brandName}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <button 
              onClick={() => setContactModalOpen(true)}
              className="hover:text-white transition-colors"
              data-testid="footer-contact"
            >
              Contact
            </button>
            <p>© {new Date().getFullYear()} {brandName}. All rights reserved.</p>
            <BrandingFooter planKey={photographer?.current_plan_key} />
          </div>
        </div>
      </footer>

      {/* Floating branding badge for Starter/Pro tiers */}
      <FloatingBrandingBadge planKey={photographer?.current_plan_key} />

      {/* Contact Modal */}
      <PhotographerContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        photographerId={photographer?.photographer_id}
        photographerName={brandName}
        eventTypes={photographer?.event_types || []}
      />
    </div>
  );
};

export default PhotographerLandingPage;
